import {
  DIFF_CRITERIA, TIER_LABEL, TIER_WORD, diffMetrics, diffRated, diffScale, diffSum, rowTier,
  type DiffCriterion, type Diferenciais as DiffContent, type Diferencial, type MaterialDto, type Tier,
} from '@ads/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import * as Ic from '../components/icons';
import { Actions, useViewMeta } from '../components/Shell';
import { Tabs } from '../components/Tabs';
import { useConsolidate, useEvidence, useMaterialAction, useSaveTool, useTool, useToolReading } from '../lib/queries';
import { DiferenciaisAnalise } from './DiferenciaisAnalise';
import { Pill, type DiffFiles } from './DiffParts';
import { EvidenceModal } from './EvidenceModal';

/* ─────────────────────────────────────────────────────────────────────
   Diferenciais Competitivos. Preenchimento: a trinca — três notas de 1 a
   5 para cada diferencial — e as evidências. As somas por linha, por
   coluna e o total se refazem a cada clique; o agente lê as notas e os
   anexos e devolve a sua própria trinca, que aparece ao lado (a força
   real). Análise consolidada: o painel que o agente monta com isso.
   ───────────────────────────────────────────────────────────────────── */

const SAVE_MS = 800;
const MAX = 12;
const SCALE: Record<number, string> = {
  1: 'Discordo totalmente', 2: 'Discordo parcialmente', 3: 'Tenho dúvida / não há evidência suficiente', 4: 'Concordo parcialmente', 5: 'Concordo totalmente',
};
const newId = () => `dif-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
/** a força (0–100) acompanha a trinca, para o DNA */
const derive = (d: Diferencial): Diferencial => {
  const s = diffSum(d);
  return { ...d, strength: s ? Math.round((s / 15) * 100) : 0 };
};

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export function Diferenciais() {
  useViewMeta({ nav: 'strategy', crumb: ['Strategy', 'Diferenciais Competitivos'], j: .29 });
  const nav = useNavigate();
  const tool = useTool('diferenciais');
  const save = useSaveTool('diferenciais');
  const evidence = useEvidence();
  const q = useToolReading('diferenciais');
  const consolidate = useConsolidate();
  const act = useMaterialAction();
  const [tab, setTab] = useState<'fill' | 'sum'>('fill');
  const [local, setLocal] = useState<DiffContent | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [modal, setModal] = useState<{ id: string; name: string } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const pending = useRef<DiffContent | null>(null);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => { if (tool.data && !local) setLocal(tool.data.content); }, [tool.data, local]);

  /* os arquivos de cada diferencial vêm das evidências, não do que está na tela */
  const files = useMemo<DiffFiles>(() => {
    const m: DiffFiles = new Map();
    for (const e of evidence.data ?? []) {
      if (!e.ref?.startsWith('diferenciais:')) continue;
      const id = e.ref.slice(13);
      m.set(id, [...(m.get(id) ?? []), e]);
    }
    return m;
  }, [evidence.data]);
  const content = useMemo<DiffContent | null>(() => (local ? {
    items: local.items.map(d => ({ ...d, files: evidence.data ? files.get(d.id ?? '')?.length ?? 0 : d.files })),
  } : null), [local, files, evidence.data]);

  const r = q.data;
  const reading = r?.reading ?? null;
  const updating = !!r && (r.pending || r.stale);
  const m = useMemo(() => (content ? diffMetrics(content, reading) : null), [content, reading]);
  const ia = useMemo(() => new Map((reading?.items ?? []).map(x => [x.id, x])), [reading]);

  const flush = useCallback(() => {
    clearTimeout(timer.current);
    const next = pending.current;
    if (!next) return;
    pending.current = null;
    setSaveState('saving');
    save.mutate(next, {
      onSuccess: () => setSaveState(s => (pending.current ? s : 'saved')),
      onError: () => { setSaveState('error'); pending.current ??= next; },
    });
  }, [save]);
  const commit = (next: DiffContent) => {
    setLocal(next);
    pending.current = next;
    setSaveState('saving');
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, SAVE_MS);
  };
  useEffect(() => () => { flush(); }, [flush]);

  const patch = (i: number, p: Partial<Diferencial>) => {
    if (!local) return;
    commit({ items: local.items.map((d, k) => (k === i ? derive({ ...d, ...p }) : d)) });
  };
  const addRow = () => {
    if (!local || local.items.length >= MAX) return;
    const row: Diferencial = { id: newId(), label: '', description: '', real: 0, distinct: 0, hard: 0, strength: 0, evidence: '', files: 0 };
    commit({ items: [...local.items, row] });
    requestAnimationFrame(() => wrap.current?.querySelector<HTMLTextAreaElement>('tbody tr:last-child textarea')?.focus());
  };
  const removeRow = (i: number) => {
    if (!local) return;
    /* as evidências do diferencial saem junto */
    const id = local.items[i]?.id;
    for (const e of (id && files.get(id)) || []) act.mutate({ id: e.id, action: 'delete' });
    commit({ items: local.items.filter((_, k) => k !== i) });
  };

  const status = saveState === 'saving' ? 'Salvando…' : saveState === 'saved' ? 'Salvo' : saveState === 'error' ? 'Não salvou — tentando de novo' : '';
  const scale = diffScale(m?.n || 3);
  const go = (t: 'fill' | 'sum') => { flush(); setTab(t); document.querySelector('.stage')?.scrollTo({ top: 0, behavior: 'smooth' }); };

  return (
    <section className="view on" data-v="diferenciais">
      <button className="back" type="button" onClick={() => { flush(); nav('/strategy'); }}><Ic.ChevronLeft />Voltar para Strategy</button>
      <div className="vhead">
        <div>
          <div className="eyebrow"><span className="rule" /><span>Strategy</span><b>Ferramenta 02 / 10</b></div>
          <h1>Diferenciais Competitivos</h1>
          <p className="lede">{tab === 'fill'
            ? 'Avalie o quanto a organização realmente possui cada diferencial, em relação ao que é esperado no mercado.'
            : 'A IA consolidou a força real, a consistência, a qualidade das evidências e as recomendações dos diferenciais.'}</p>
        </div>
        <div className="tools">
          {status && <span className={`g-save ${saveState}`} aria-live="polite">{saveState === 'saved' && <Ic.Check />}{status}</span>}
          <Tabs options={[['fill', 'Preenchimento'], ['sum', 'Análise consolidada']] as const} value={tab} onChange={go} />
        </div>
      </div>

      {!content || !m ? <div className="loading-line" /> : tab === 'fill' ? (
        <>
          <div className="d-top">
            <div className="blocks-head">
              <div>
                <h2>A trinca</h2>
                <p className="subhead">Dê uma nota de 1 a 5 em cada critério. A soma da linha é a força do diferencial; a da coluna, a força do critério.</p>
              </div>
            </div>
            <div className="notice d-tip"><Ic.Bulb />
              <p><b>Dica da IA.</b> Use evidências concretas e verificáveis — documentos, dados, depoimentos. Quanto mais robusta a evidência, mais confiável a força real calculada pelo ADS.</p>
            </div>
          </div>

          <div className="tablewrap nomask" ref={wrap}>
            <table className="dense dtable">
              <thead>
                <tr>
                  <th rowSpan={2} className="d-no">#</th>
                  <th rowSpan={2} className="d-name-h">Diferenciais<span>Liste os principais diferenciais da organização.</span></th>
                  <th colSpan={3} className="d-group">Classificação<span>1 discordo totalmente · 2 discordo parcialmente · 3 tenho dúvida · 4 concordo parcialmente · 5 concordo totalmente</span></th>
                  <th rowSpan={2} className="d-ev-h">Evidência<span>Anexe provas a cada diferencial.</span></th>
                  <th rowSpan={2} className="d-force-h">Força do diferencial<span>Soma das 3 notas</span></th>
                </tr>
                <tr>{DIFF_CRITERIA.map(c => <th key={c.key} className="d-crit-h">{c.label}</th>)}</tr>
              </thead>
              <tbody>
                {content.items.map((d, i) => (
                  <DiffRow key={d.id ?? i} n={i + 1} d={d} ia={ia.get(d.id ?? '') ?? null} stale={updating}
                    files={files.get(d.id ?? '') ?? []}
                    onPatch={p => patch(i, p)} onRemove={() => removeRow(i)}
                    onEvidence={() => setModal({ id: d.id!, name: d.label || `Diferencial ${i + 1}` })} />
                ))}
                {!content.items.length && (
                  <tr><td colSpan={7} className="d-empty">Nenhum diferencial ainda. Comece pelo que a organização faz melhor que as outras.</td></tr>
                )}
              </tbody>
              {m.n > 0 && (
                <tfoot>
                  <tr className="d-sum">
                    <td colSpan={2}><b>Escala da trinca</b><span>Total por coluna · sua avaliação</span></td>
                    {DIFF_CRITERIA.map(c => <td key={c.key} className="d-col" data-label={c.short}><Pill value={m.cols.user[c.key].sum} tier={m.cols.user[c.key].tier} /></td>)}
                    <td className="d-tot-l"><b>Força total</b><span>Soma de todos</span></td>
                    <td><Pill value={m.total.user.sum} tier={m.total.user.tier} max={m.max} big /></td>
                  </tr>
                  <tr className={`d-sum ia${updating ? ' is-stale' : ''}`}>
                    <td colSpan={2}><b><Ic.Sparkle />Força real · IA</b><span>{m.cols.ia ? 'A trinca que as evidências sustentam' : updating ? 'O ADS está lendo notas e evidências…' : 'Aparece depois das primeiras notas'}</span></td>
                    {DIFF_CRITERIA.map(c => <td key={c.key} className="d-col" data-label={c.short}>{m.cols.ia ? <Pill value={m.cols.ia[c.key].sum} tier={m.cols.ia[c.key].tier} /> : <span className="d-dash">—</span>}</td>)}
                    <td className="d-tot-l"><b>Força real total</b><span>{m.total.ia ? TIER_LABEL[m.total.ia.tier] : '—'}</span></td>
                    <td>{m.total.ia ? <Pill value={m.total.ia.sum} tier={m.total.ia.tier} max={m.max} big /> : <span className="d-dash">—</span>}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {content.items.length < MAX
            ? <button className="addlink" type="button" onClick={addRow}><Ic.Plus />Adicionar diferencial</button>
            : <p className="subhead">Até {MAX} diferenciais — concentre nos que mais pesam na escolha do cliente.</p>}

          <div className="d-legend">
            <section className="card">
              <h3>Escala da trinca <em>por coluna</em></h3>
              <Scale rows={scale.col} />
            </section>
            <section className="card">
              <h3>Escala consolidada <em>soma total</em></h3>
              <Scale rows={scale.total} />
            </section>
            <section className="card d-next">
              <h3><Ic.Sparkle />Próxima etapa</h3>
              <p>Na Análise consolidada, a IA interpreta as notas e as evidências, identifica padrões e gera recomendações. Evidências mais robustas tornam a leitura mais confiável.</p>
              <button type="button" className="d-link" onClick={() => go('sum')}>Ver a análise consolidada<Ic.ArrowRight /></button>
            </section>
          </div>
        </>
      ) : (
        <DiferenciaisAnalise content={content} files={files} onEdit={() => go('fill')}
          onEvidence={(id, name) => setModal({ id, name })} />
      )}

      {modal && (
        <EvidenceModal onClose={() => setModal(null)} target={{
          ref: `diferenciais:${modal.id}`, title: modal.name, kicker: 'Evidências · Diferenciais Competitivos',
          lede: 'Provas de que o diferencial existe e se destaca — relatórios, pesquisas, dados, depoimentos, prêmios, contratos. O ADS lê cada documento e recalcula a força real.',
          empty: 'Sem evidência, a IA desconta a nota declarada até haver prova.',
        }} />
      )}

      <Actions>
        <button className="btn-ghost" type="button" onClick={() => { flush(); nav('/strategy'); }}>Salvar e sair</button>
        <button className="btn-ghost" type="button" onClick={() => (tab === 'sum' ? go('fill') : (flush(), nav('/strategy/gaps')))}>Voltar</button>
        <button className="btn-primary" type="button" disabled={consolidate.isPending}
          onClick={() => {
            if (tab === 'fill') { go('sum'); return; }
            flush(); consolidate.mutate(undefined, { onSettled: () => nav('/strategy/triade') });
          }}>
          {consolidate.isPending ? 'Consolidando o DNA…' : 'Continuar'}<Ic.ArrowRight />
        </button>
      </Actions>
    </section>
  );
}

/* ── uma linha ────────────────────────────────────────────────────── */

type IaItem = { real: number; distinct: number; hard: number; leitura: string } | null;

function DiffRow({ n, d, ia, stale, files, onPatch, onRemove, onEvidence }: {
  n: number; d: Diferencial; ia: IaItem; stale: boolean; files: MaterialDto[];
  onPatch: (p: Partial<Diferencial>) => void; onRemove: () => void; onEvidence: () => void;
}) {
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { if (!confirm) return; const t = setTimeout(() => setConfirm(false), 3500); return () => clearTimeout(t); }, [confirm]);
  const name = d.label || `o diferencial ${n}`;
  const sum = diffSum(d);
  const rated = diffRated(d);
  const iaSum = ia ? ia.real + ia.distinct + ia.hard : null;
  const count = files.length;
  return (
    <tr>
      <td className="d-no mono">{n}</td>
      <td className="g-name d-name">
        <textarea className="gcell strong" rows={1} value={d.label} placeholder="Nome do diferencial" aria-label={`Diferencial ${n}`}
          maxLength={160} onChange={e => onPatch({ label: e.target.value })} />
        <textarea className="gcell d-sub" rows={1} value={d.description} placeholder="O que é, na prática (opcional)" aria-label={`Descrição — ${name}`}
          maxLength={400} onChange={e => onPatch({ description: e.target.value })} />
        {confirm
          ? <button type="button" className="g-del" onClick={onRemove}>{count ? `Excluir com ${count} ${count === 1 ? 'evidência' : 'evidências'}` : 'Excluir diferencial'}</button>
          : <button type="button" className="g-x" onClick={() => setConfirm(true)} aria-label={`Remover ${name}`}><Ic.Close /></button>}
      </td>
      {DIFF_CRITERIA.map(c => (
        <td key={c.key} className="d-crit" data-label={c.label}>
          <Trinca value={d[c.key]} ia={ia?.[c.key] ?? null} stale={stale} label={`${c.label.replace(/\.$/, '')} — ${name}`}
            onChange={v => onPatch({ [c.key]: v } as Pick<Diferencial, DiffCriterion>)} />
        </td>
      ))}
      <td className="d-ev" data-label="Evidência">
        <button type="button" className={`evid g-evid${count ? '' : ' none'}`} onClick={onEvidence} disabled={!d.id}>
          <Ic.Clip />{count ? `${count} arquivo${count > 1 ? 's' : ''}` : 'Anexar evidência'}
        </button>
        {count > 0 && <span className="d-files" title={files.map(f => f.filename).join('\n')}>{files[0]!.catalog?.title ?? files[0]!.filename}{count > 1 ? ` +${count - 1}` : ''}</span>}
        <textarea className="gcell d-sub" rows={1} value={d.evidence} placeholder="Ou descreva a prova em uma frase" aria-label={`Evidência em texto — ${name}`}
          maxLength={400} onChange={e => onPatch({ evidence: e.target.value })} />
      </td>
      <td className="d-force" data-label="Força">
        {rated ? <Pill value={sum} tier={rowTier(sum)} max={15} big /> : <span className="d-part" title="Faltam notas">{sum ? `${sum}/15` : '—'}</span>}
        {iaSum != null && (
          <span className={`d-ia-sum${stale ? ' is-stale' : ''}`} title={ia?.leitura}>
            <Ic.Sparkle />IA <b className={`t-${rowTier(iaSum)}`}>{iaSum}</b> · {TIER_WORD[rowTier(iaSum)]}
          </span>
        )}
      </td>
    </tr>
  );
}

/** Cinco botões de 1 a 5; a nota da IA fica marcada com um anel. */
function Trinca({ value, ia, stale, label, onChange }: { value: number; ia: number | null; stale: boolean; label: string; onChange: (v: number) => void }) {
  const dir = ia == null || !value ? null : ia > value ? 'up' : ia < value ? 'down' : 'eq';
  return (
    <div className="trinca-w">
      <span className="trinca" role="radiogroup" aria-label={label}>
        {[1, 2, 3, 4, 5].map(l => (
          <button key={l} type="button" role="radio" aria-checked={value === l}
            className={`${value === l ? 'on' : ''}${ia === l ? ' ia' : ''}${stale ? ' stale' : ''}`}
            title={`${l} — ${SCALE[l]}${ia === l ? ' · nota da IA' : ''}`}
            onClick={() => onChange(value === l ? 0 : l)}>{l}</button>
        ))}
      </span>
      {ia != null && (
        <span className={`t-ia ${dir ?? ''}${stale ? ' is-stale' : ''}`}>
          IA {ia}{dir === 'up' ? ' ↑' : dir === 'down' ? ' ↓' : dir === 'eq' ? ' ✓' : ''}
        </span>
      )}
    </div>
  );
}

function Scale({ rows }: { rows: Array<{ tier: Tier; from: number; to: number }> }) {
  return (
    <ul className="d-scale">
      {rows.map(r => (
        <li key={r.tier} className={`t-${r.tier}`}><i />{r.from} a {r.to} pontos<span>{TIER_LABEL[r.tier]}</span></li>
      ))}
    </ul>
  );
}

